import React, { useEffect, useRef, useState } from "react";
import { ICitationSentence, IEpiProps, ISearchResultsContainer } from "../models/vertex-search-page-models";
import { useVertexSearchContext } from "../reducers/vertex-search-page-provider";
import { SearchAnswerCitations } from "./search-answer-citations";

interface IProps {
    onRelatedQuestionClick: (relatedQuestion: string) => void;
}

interface IContextProps {
    epiProps: IEpiProps
    searchResultsContainer: ISearchResultsContainer
}

export const SearchAnswer = (props: IProps) => {
    const { epiProps, searchResultsContainer }: IContextProps = useVertexSearchContext();

    if (searchResultsContainer.searchAnswer == null) return null;
    function formatResponse(sentence: string) {
        sentence = sentence.replace(/\n/g, "<br />"); //Add line breaks where Google returned the new line character.
        sentence = handleBolding(sentence);
        return sentence;
    }
    function handleBolding(text: string): string {
        // Replace **text** with <b>text</b>
        return text.replace(/\*\*(.+?)\*\*/g, '<b>$1</b>');
    }

    // Helper to group sentences into paragraphs by splitting on \n\n
    function groupSentencesIntoParagraphs(): ICitationSentence[][] {

        if (searchResultsContainer == null || searchResultsContainer.searchAnswer == null
            || searchResultsContainer.searchAnswer.citationSentences == null
            || searchResultsContainer.searchAnswer.citationSentences.length == 0) {
            return null;
        }


        let citationSentences = searchResultsContainer.searchAnswer.citationSentences;

        const paragraphs: ICitationSentence[][] = [];
        let currentParagraph: ICitationSentence[] = [];

        for (const sentence of citationSentences) {
            if (sentence.citationSentenceText.includes('\n\n')) {
                const parts = sentence.citationSentenceText.split(/\n\n/);

                // Add the part before \n\n to the current paragraph
                if (parts[0]) {
                    currentParagraph.push({
                        ...sentence,
                        citationSentenceText: parts[0]
                    });
                }

                // Push the current paragraph if it has any sentences
                if (currentParagraph.length > 0) {
                    paragraphs.push(currentParagraph);
                    currentParagraph = [];
                }

                // If there's text after the \n\n, start a new paragraph with it
                if (parts[1]) {
                    currentParagraph.push({
                        ...sentence,
                        citationSentenceText: parts.slice(1).join('\n\n')
                    });
                }
            } else {
                currentParagraph.push(sentence);
            }
        }

        // Add any remaining sentences as the last paragraph
        if (currentParagraph.length > 0) {
            paragraphs.push(currentParagraph);
        }

        return paragraphs;
    }

    const paragraphs = groupSentencesIntoParagraphs();

    const collapsedHeight = 380;
    const maxContentHeight = 450;

    const contentRef = useRef(null);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [shouldCollapse, setShouldCollapse] = useState(false);

    useEffect(() => {
        if (contentRef.current) {
            const contentHeight = contentRef.current.scrollHeight;
            setShouldCollapse(contentHeight > maxContentHeight);
            setIsCollapsed(contentHeight > maxContentHeight);
        }
    }, []);


    return (
        <div>
            {
                paragraphs != null && paragraphs.length > 0
                && 
                <div className={`vertex-search-overview ${isCollapsed ? " vertex-search-overview-collapsed" : ""} `}>


                    <div className={`vertex-search-overview-content ${isCollapsed ? " vertex-search-overview-content-collapsed" : ""}`} ref={contentRef} style={{ maxHeight: isCollapsed ? collapsedHeight : undefined }}>
                        <div className="vertex-search-overview-header">
                            <svg className="svg-icon" aria-hidden="true" focusable="false">
                                <use xlinkHref="#icon-overview"></use>
                            </svg>
                            <h5>{epiProps.generativeAIOverviewHeading}</h5>
                        </div>
                        {paragraphs != null && paragraphs.length > 0
                            && paragraphs.map((sentences, pIdx) => (
                                <p className="paragraph-large" key={pIdx}>
                                    {sentences.map((sentence, sIdx) => {
                                        const hasText = sentence.citationSentenceText && sentence.citationSentenceText.trim().length > 0;
                                        const hasReferences = sentence.citations && sentence.citations.length > 0;
                                        return (
                                            <React.Fragment key={sIdx}>
                                                <span dangerouslySetInnerHTML={{ __html: formatResponse(sentence.citationSentenceText) }}></span>
                                                {hasText && hasReferences && <SearchAnswerCitations citationSentence={sentence} />}
                                            </React.Fragment>
                                        );
                                    })}
                                </p>
                            ))}
                    </div>
                    {shouldCollapse &&
                        <button className={`vertex-search-overview-show-hide-button ${isCollapsed ? " vertex-search-overview-show-hide-button-expanded" : ""}`} onClick={() => setIsCollapsed((v) => !v)}>{isCollapsed ? "Show more" : "Show less"} <svg className="svg-icon" aria-hidden="true" focusable="false">
                            <use xlinkHref="#icon-chevron-down"></use>
                        </svg></button>
                    }
                </div>
            }
            {
                paragraphs != null && paragraphs.length > 0
                && 
                <div>
                    <p>{epiProps.generativeAIOverviewDisclaimer}</p>
                </div>
            }
            {
                searchResultsContainer?.searchAnswer?.relatedQuestions && searchResultsContainer?.searchAnswer?.relatedQuestions.length > 0
                &&
                <div>
                    <h5>{epiProps.relatedQuestionsHeading}</h5>
                    <ul className="unlisted vertex-search-related-list">
                        {searchResultsContainer.searchAnswer.relatedQuestions.map(relatedQuestion => (
                            <li className="vertex-search-related-list-item">
                                <button className="vertex-search-related-button" onClick={() => props.onRelatedQuestionClick(relatedQuestion)}>{relatedQuestion}
                                    <svg className="svg-icon" aria-hidden="true" focusable="false">
                                        <use xlinkHref="#icon-right-angle-arrow"></use>
                                    </svg>
                                </button></li>
                        ))}
                    </ul>
                </div>
            }

        </div>
    )
}